import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, TrendingUp, Clock, Users, Star } from 'lucide-react';
import { FloatingPaths } from '@LandingFunctionalDisplay/BackgroundPaths';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Footer from '@CommonFunctionalDisplay/Footer';
import useSEO from '@Hooks/useSEO';
import './CaseStudiesPage.scss';

const CASES = [
    {
        tag: 'Education',
        tagColor: 'purple',
        company: 'LearnSpark EdTech',
        logo: 'LS',
        summary: 'How a fast-growing online learning platform handled 10x ticket volume without adding a single support headcount.',
        metrics: [
            { icon: <TrendingUp size={16} />, value: '60%', label: 'Reduction in ticket backlog' },
            { icon: <Clock size={16} />, value: '2.4 hrs', label: 'Avg resolution time (down from 14)' },
            { icon: <Users size={16} />, value: '12K+', label: 'Students supported monthly' },
        ],
        quote: 'KaizIQ Resolve turned our support chaos into a structured, accountable process. Our students now know exactly what\'s happening with their query — and so do we.',
        author: 'Priya Nair',
        role: 'Head of Student Success, LearnSpark',
        featured: true,
    },
    {
        tag: 'Healthcare',
        tagColor: 'blue',
        company: 'MediLink Clinics',
        logo: 'ML',
        summary: 'A multi-location clinic chain that replaced email threads with structured patient query management and WhatsApp updates.',
        metrics: [
            { icon: <Clock size={16} />, value: '15 min', label: 'First response time' },
            { icon: <Star size={16} />, value: '4.7★', label: 'Patient satisfaction score' },
            { icon: <TrendingUp size={16} />, value: '80%', label: 'Fewer missed follow-ups' },
        ],
        quote: 'Our patients were frustrated by slow responses. Now they get a WhatsApp update the moment their query is assigned. It\'s changed how they perceive us.',
        author: 'Dr. Ramesh Iyer',
        role: 'COO, MediLink Clinics',
        featured: false,
    },
    {
        tag: 'Retail',
        tagColor: 'green',
        company: 'StyleCart',
        logo: 'SC',
        summary: 'An e-commerce fashion brand that deployed a white-label customer support app and cut complaint resolution time in half.',
        metrics: [
            { icon: <Clock size={16} />, value: '50%', label: 'Faster complaint resolution' },
            { icon: <TrendingUp size={16} />, value: '3x', label: 'More organised ticket queue' },
            { icon: <Users size={16} />, value: '25K+', label: 'Customers served per month' },
        ],
        quote: 'The white-label mobile app was a game-changer. Customers can track their complaints from their phone, and our team has full visibility into what\'s pending.',
        author: 'Aditi Sharma',
        role: 'Customer Experience Lead, StyleCart',
        featured: false,
    },
];

const TAG_COLORS = {
    blue:   { bg: 'rgba(26,111,181,0.18)',  text: '#3b9aee' },
    green:  { bg: 'rgba(5,150,105,0.18)',   text: '#10b981' },
    purple: { bg: 'rgba(124,58,237,0.18)',  text: '#a78bfa' },
};

function Reveal({ children, className = '', delay = 0 }) {
    return (
        <motion.div className={className}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay }}
        >{children}</motion.div>
    );
}

export default function CaseStudiesPage() {
    const navigate = useNavigate();
    const cn = 'caseStudiesPage';

    const seo = useSEO({
        title:       'Case Studies — Real Teams, Real Results',
        description: 'See how LearnSpark EdTech, MediLink Clinics, and StyleCart transformed their support operations with KaizIQ Resolve. Real metrics, real outcomes.',
        canonical:   'https://resolve.kaiziq.io/#/case-studies',
        schema: {
            '@context': 'https://schema.org',
            '@type':    'WebPage',
            name:       'KaizIQ Resolve Case Studies',
            url:        'https://resolve.kaiziq.io/#/case-studies',
            description: 'Customer success stories from organisations using KaizIQ Resolve.',
        },
    });

    return (
        <div className={cn}>
            {seo}
            <TopPanel />

            <section className={cn + '__hero'}>
                <FloatingPaths position={1} />
                <FloatingPaths position={-1} />
                <div className={cn + '__hero__inner'}>
                    <motion.div className={cn + '__hero__tag'} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                        Case Studies
                    </motion.div>
                    <motion.h1 className={cn + '__hero__title'} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}>
                        Real teams. Real results.
                    </motion.h1>
                    <motion.p className={cn + '__hero__sub'} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}>
                        Discover how organisations across industries use KaizIQ Resolve to transform their support operations.
                    </motion.p>
                </div>
            </section>

            <section className={cn + '__studies'}>
                {CASES.map((cs, i) => {
                    const tagStyle = TAG_COLORS[cs.tagColor] || TAG_COLORS.blue;
                    return (
                        <Reveal key={i} className={cn + '__study' + (cs.featured ? ' ' + cn + '__study--featured' : '')} delay={i * 0.1}>
                            <div className={cn + '__study__left'}>
                                <div className={cn + '__study__logo'}>{cs.logo}</div>
                                <span className={cn + '__study__tag'} style={{ background: tagStyle.bg, color: tagStyle.text }}>{cs.tag}</span>
                                <h3 className={cn + '__study__company'}>{cs.company}</h3>
                                <p className={cn + '__study__summary'}>{cs.summary}</p>
                                <div className={cn + '__study__metrics'}>
                                    {cs.metrics.map((m, mi) => (
                                        <div key={mi} className={cn + '__study__metric'}>
                                            <span className={cn + '__study__metric__value'}>{m.value}</span>
                                            <span className={cn + '__study__metric__label'}>{m.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={cn + '__study__right'}>
                                <blockquote className={cn + '__study__quote'}>
                                    <p>"{cs.quote}"</p>
                                    <footer>
                                        <strong>{cs.author}</strong>
                                        <span>{cs.role}</span>
                                    </footer>
                                </blockquote>
                            </div>
                        </Reveal>
                    );
                })}
            </section>

            <section className={cn + '__cta'}>
                <Reveal className={cn + '__cta__inner'}>
                    <h2 className={cn + '__cta__title'}>Write your own success story</h2>
                    <p className={cn + '__cta__sub'}>Join the teams transforming their support operations with KaizIQ Resolve.</p>
                    <button className={cn + '__cta__btn'} onClick={() => navigate('/login')}>
                        Start Free Trial <ArrowRight size={16} />
                    </button>
                </Reveal>
            </section>

            <Footer />
        </div>
    );
}
